<template>
    <div class="main-body">
        <h2>{{ $strings('cpsite/app/title') }}</h2>
        <div class="action-bar">
            <div class="add-wrapper">
                <el-button size="small" type="primary" @click="onCreate">{{ $strings('word/create') }}</el-button>
            </div>
            <div class="filter-wrapper">
                <el-form :inline="true" :model="formFilter" class="demo-form-inline" ref="filter">
                    <el-form-item label="">
                        <el-input v-model="formFilter.app_name" :placeholder="$strings('cpsite/app/app_name')"
                                  size="small"
                                  class="max-width-120" clearable></el-input>
                    </el-form-item>
                    <el-form-item>
                        <el-button @click="onFilter" size="small">{{ $strings('word/query') }}</el-button>
                        <el-button @click="onReset" size="small">{{ $strings('word/reset') }}</el-button>
                    </el-form-item>
                </el-form>
            </div>
        </div>
        <div class="data-area">
            <el-table v-loading="loading" border :data="tableData" style="width: 100%" highlight-current-row>
                <el-table-column prop="app_name" :label="$strings('cpsite/app/app_name')" width="180">
                    <template slot-scope="scope">
                        <el-link @click="toAppReport(scope.row.app_id)"
                                 v-if="scope.row.app_status === appStatus.LIVING">{{ scope.row.app_name }}
                        </el-link>
                        <span v-else>{{ scope.row.app_name }}</span>
                    </template>
                </el-table-column>
                <el-table-column prop="app_id" :label="$strings('cpsite/app/app_id')" width="160">
                    <template slot-scope="scope"><span class="code-text">{{ scope.row.app_id }}</span></template>
                </el-table-column>
                <el-table-column prop="app_token" :label="$strings('cpsite/app/app_token')"
                                 width="300">
                    <template slot-scope="scope"><span class="code-text">{{ scope.row.app_token }}</span></template>
                </el-table-column>
                <el-table-column prop="app_status" :label="$strings('cpsite/app/app_status')"
                                 width="90">
                    <template slot="header">
                        <el-tooltip class="item" effect="dark" :content="$strings('cpsite/app/app_status_str_tooltip')"
                                    placement="top-start">
                            <span><i class="el-icon-info"></i>&nbsp;{{ $strings('cpsite/app/app_status') }}</span>
                        </el-tooltip>
                    </template>
                    <template slot-scope="scope">
                        <el-tooltip class="item" effect="dark" :content="scope.row.remark" placement="top-start"
                                    v-if="scope.row.app_status*1 === appStatus.REJECTED">
                            <el-link :type="$strings('cpsite/app/app_status_type/' +  scope.row.app_status)"
                                     class="text-bold">
                                {{ $strings('cpsite/app/app_status_str/' + scope.row.app_status) }}
                            </el-link>
                        </el-tooltip>
                        <el-link :type="$strings('cpsite/app/app_status_type/' +  scope.row.app_status)"
                                 class="text-bold" v-else :underline="false">
                            {{ $strings('cpsite/app/app_status_str/' + scope.row.app_status) }}
                        </el-link>
                    </template>
                </el-table-column>
                <el-table-column prop="smpp_host" :label="$strings('word/smpp_host')" width="90"></el-table-column>
                <el-table-column prop="smpp_port" :label="$strings('word/smpp_port')" width="90"></el-table-column>
                <el-table-column prop="smpp_user" :label="$strings('word/smpp_user')" width="90"></el-table-column>
                <el-table-column prop="smpp_pass" :label="$strings('word/smpp_pass')" width="90"></el-table-column>
                <el-table-column prop="smpp_sender" :label="$strings('word/smpp_sender')" width="90"></el-table-column>
                <el-table-column prop="ct_time" :label="$strings('word/ct_time')" width="160" sortable>
                    <template slot-scope="scope">
                        {{ convertUnixToDate(scope.row.ct_time) }}
                    </template>
                </el-table-column>
                <el-table-column prop="app_purpose" :label="$strings('cpsite/app/app_purpose')"
                                 show-overflow-tooltip></el-table-column>
                <el-table-column width="100" :label="$strings('word/edit')">
                    <template slot-scope="scope">
                        <el-button size="small" @click="onEdit(scope.row)">{{ $strings('word/edit') }}</el-button>
                    </template>
                </el-table-column>
            </el-table>
            <div class="pagination margin-top-1" v-if="pagination.total > pagination.size" style="text-align: right">
                <el-pagination background layout="prev, pager, next" :total="pagination.total"
                               :page-size.sync="pagination.size"
                               :current-page.sync="pagination.page" @current-change="refreshData"></el-pagination>
            </div>
        </div>
        <el-dialog
            :title="$strings('word/create')"
            :visible.sync="dialogVisible"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            :show-close="false"
            @closed="onClose"
            width="30%">
            <div>
                <el-form ref="form" :model="editForm" label-width="80px" :rules="rules">
                    <el-form-item :label="$strings('cpsite/app/app_name')" prop="app_name">
                        <el-input v-model="editForm.app_name" clearable maxlength="24" show-word-limit></el-input>
                    </el-form-item>
                    <el-form-item :label="$strings('cpsite/app/app_purpose')" prop="app_purpose">
                        <el-input v-model="editForm.app_purpose" clearable maxlength="24" show-word-limit></el-input>
                    </el-form-item>
                </el-form>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-button @click="dialogVisible = false">{{ $strings('word/cancel') }}</el-button>
                <el-button type="primary" @click="onSaveEdit()" :loading="saving">{{ $strings('word/confirm') }}
                </el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {API_STATUS_CODE, APP_STATUS, STORAGE_KEY_LOGIN_TZ} from "@/config/config";
import {GetItem} from "@/utils/storage";
import {CpSitAppRead, CpSiteAppCreate} from "@/api/cpsite/api";
import {UnixToDateFull} from "@/utils/time";
import {CpSiteAppUpdate} from "../../../api/cpsite/api";

export default {
    name: "cpsite-app-index",
    data() {
        return {
            loading: true,
            saving: false,
            editForm: {},
            isEdit: false,
            formFilter: {},
            tableData: [],
            dialogVisible: false,
            rules: {
                app_name: [
                    {required: true, message: this.$strings('cpsite/app/error/field_empty'), trigger: 'blur'}
                ],
            },
            pagination: {
                total: 0,
                page: 1,
                size: 8,
            },
            appStatus: APP_STATUS,
        }
    },
    mounted() {
        this.refreshData();
    },
    computed: {},
    methods: {
        convertUnixToDate(ts) {
            let tz = parseInt(GetItem(STORAGE_KEY_LOGIN_TZ))
            return UnixToDateFull(ts, tz);
        },
        toAppReport(appID) {
            this.$router.push('/cpsite/dash/' + appID);
        },
        onFilter() {
            this.refreshData();
        },
        onReset() {
            this.formFilter = {};
            this.refreshData();
        },
        onClose() {
            this.saving = false
            this.$refs["form"].clearValidate();
        },
        onCreate() {
            this.isEdit = false;
            this.editForm = {};
            this.dialogVisible = true;
        },
        onEdit(row) {
            this.isEdit = true;
            this.editForm = JSON.parse(JSON.stringify(row));
            this.dialogVisible = true;
        },
        onSaveEdit() {
            this.saving = true;
            let action = this.isEdit ? CpSiteAppUpdate : CpSiteAppCreate;
            action(this.editForm).then((resp) => {
                if (resp.data.code === API_STATUS_CODE.SUCCESS) {
                    this.dialogVisible = false;
                    this.refreshData();
                }
            });
        },
        refreshData() {
            this.loading = true;
            this.formFilter.size = this.pagination.size;
            this.formFilter.page = this.pagination.page;
            CpSitAppRead(this.formFilter).then((resp) => {
                this.pagination.total = resp.data.data.total;
                this.tableData = resp.data.data.rows;
            }).finally(() => {
                this.loading = false;
            })
        },
    }
}
</script>

<style scoped lang="scss">
.action-bar {
    display: flex;
    flex-direction: row;

    .filter-wrapper {
        display: flex;
        justify-content: flex-end;
        flex-grow: 1;
    }

    .add-wrapper {
        padding-top: 4px;
    }
}

.table-pagination {
    margin-top: 1em;
    display: flex;
    justify-content: flex-end;
}
</style>